import { useState, useEffect } from 'react'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Calendar as CalendarIcon, Plus, Clock, MapPin, Users, Video, Trash2, Edit, Tag, Mail, Sparkles, Settings } from 'lucide-react'
import { motion } from 'framer-motion'
import { toast } from 'sonner'
import { calendarService } from '@/services/calendarService'
import { calendarAI } from '@/services/calendarAI'
import { sampleCalendarEvents } from '@/services/sampleData'
import { createClient } from '@blinkdotnew/sdk'
import { getConnectionStatus, getValidAccessToken } from '@/services/oauthService'
import type { CalendarEvent } from '@/types/calendar'
import { AdvancedCalendar } from '@/components/calendar/AdvancedCalendar'
import { Switch } from '@/components/ui/switch'
import { MeetingPrepScheduler } from '@/components/calendar/MeetingPrepScheduler'
import type { PrepTimeSlot } from '@/components/calendar/MeetingPrepScheduler'
import { format, startOfWeek, endOfWeek, addWeeks, startOfQuarter, endOfQuarter } from 'date-fns'
import { safeJsonParse } from '@/lib/utils';

const blink = createClient({
  projectId: 'lemon-ui-agentic-email-ai-agent-ben3ou87',
  authRequired: false
})

export default function CalendarPage() {
  // Initialize to today's date with proper time
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  
  const [events, setEvents] = useState<CalendarEvent[]>(sampleCalendarEvents)
  const [loading, setLoading] = useState(false)
  const [user, setUser] = useState<any>(null)
  const [isDemoMode, setIsDemoMode] = useState(true)
  const [showNewEventDialog, setShowNewEventDialog] = useState(false)
  const [editingEvent, setEditingEvent] = useState<CalendarEvent | null>(null)
  const [categorizingEvents, setCategorizingEvents] = useState(false)
  const [creatingEvent, setCreatingEvent] = useState(false)
  const [narrowWeekend, setNarrowWeekend] = useState(true)
  const [startDayOfWeek, setStartDayOfWeek] = useState<0 | 1 | 2 | 3 | 4 | 5 | 6>(0)
  const [newEvent, setNewEvent] = useState({
    title: '',
    description: '',
    startTime: '',
    endTime: '',
    eventType: 'event' as 'meeting' | 'reminder' | 'task' | 'event',
    location: '',
    attendees: ''
  })
  const [lastSyncInfo, setLastSyncInfo] = useState<{ synced: number; contactsCreated: number } | null>(null)
  const [showSettings, setShowSettings] = useState(false)

  // Load user and events + real-time subscription + auto-refresh
  useEffect(() => {
    let unsubscribeRealtime: (() => void) | null = null
    let refreshInterval: number | null = null

    const unsubscribe = blink.auth.onAuthStateChanged(async (state) => {
      setUser(state.user)
      
      if (state.user) {
        console.log('📅 User authenticated:', state.user.id)
        console.log('📅 User email:', state.user.email)
        
        // User is signed in - check connection status
        const status = await getConnectionStatus(state.user.id)
        const hasConnections = status.gmail || status.outlook
        
        console.log('📅 Connection status:', status)
        
        // User is signed in - load real data from database
        setIsDemoMode(false)
        loadEvents(state.user.id)
        
        // Auto-sync calendar on load if connected
        if (hasConnections) {
          autoSyncCalendars(state.user.id)
        }

        // Subscribe to real-time calendar updates
        try {
          unsubscribeRealtime = await blink.realtime.subscribe(
            `calendar-${state.user.id}`,
            (message) => {
              if (message.type === 'new-event' || message.type === 'event-updated') {
                console.log('Real-time calendar update received, reloading...')
                loadEvents(state.user.id, true) // Skip auto-sync on realtime update
              }
            }
          )
        } catch (error) {
          console.log('Real-time calendar subscription unavailable')
        }

        // Set up background refresh every 30 seconds to reload from DB
        refreshInterval = window.setInterval(() => {
          console.log('🔄 Background refresh: reloading events from database...')
          loadEvents(state.user.id, true) // Skip auto-sync, just reload from DB
        }, 30000) // 30 seconds
      } else {
        // Not signed in - demo mode
        setIsDemoMode(true)
        setEvents(sampleCalendarEvents)
      }
    })

    return () => {
      unsubscribe()
      if (unsubscribeRealtime) {
        unsubscribeRealtime()
      }
      if (refreshInterval) {
        clearInterval(refreshInterval)
      }
    }
  }, [])

  // Auto-sync calendar events from all connected providers
  const autoSyncCalendars = async (userId: string) => {
    try {
      // Check if user is authenticated first
      if (!blink.auth.isAuthenticated()) {
        console.log('User not authenticated - skipping calendar sync')
        toast.error('Please sign in to sync calendar')
        return { synced: 0, contactsCreated: 0 }
      }

      console.log('✅ User authenticated, checking connections...')

      // Check if user has any connected accounts
      const status = await getConnectionStatus(userId)
      const hasConnections = status.gmail || status.outlook
      
      if (!hasConnections) {
        console.log('📧 No email accounts connected - skipping calendar sync')
        toast.error('No email accounts connected. Please connect Gmail or Outlook first.')
        return { synced: 0, contactsCreated: 0 }
      }

      console.log('🔄 Auto-syncing calendars from connected providers...')
      console.log('  Gmail:', status.gmail ? '✅ Connected' : '❌ Not connected')
      console.log('  Outlook:', status.outlook ? '✅ Connected' : '❌ Not connected')

      let totalSynced = 0
      let totalContactsCreated = 0

      // Sync Gmail calendar if connected
      if (status.gmail) {
        try {
          const gmailToken = await getValidAccessToken('gmail', userId)
          if (gmailToken) {
            console.log('🔄 Syncing Gmail calendar...')
            const response = await fetch('https://ben3ou87--calendar-sync.functions.blink.new', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                provider: 'gmail',
                accessToken: gmailToken,
                userId,
                maxResults: 500
              })
            })

            if (response.ok) {
              const data = await response.json()
              console.log(`✅ Gmail calendar synced: ${data.synced} events, ${data.contactsCreated || 0} contacts created`)
              totalSynced += data.synced || 0
              totalContactsCreated += data.contactsCreated || 0
            } else {
              const error = await response.json()
              console.error('Gmail calendar sync failed:', error.error)
              toast.error(`Gmail sync failed: ${error.error}`)
            }
          } else {
            console.warn('⚠️  Gmail token not available')
            toast.warning('Gmail token expired. Please reconnect your account.')
          }
        } catch (error) {
          console.error('Gmail calendar sync error:', error)
          toast.error(`Gmail sync error: ${error instanceof Error ? error.message : 'Unknown error'}`)
        }
      }

      // Sync Outlook calendar if connected
      if (status.outlook) {
        try {
          const outlookToken = await getValidAccessToken('outlook', userId)
          if (outlookToken) {
            console.log('🔄 Syncing Outlook calendar...')
            const response = await fetch('https://ben3ou87--calendar-sync.functions.blink.new', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                provider: 'outlook',
                accessToken: outlookToken,
                userId,
                maxResults: 500
              })
            })

            if (response.ok) {
              const data = await response.json()
              console.log(`✅ Outlook calendar synced: ${data.synced} events, ${data.contactsCreated || 0} contacts created`)
              totalSynced += data.synced || 0
              totalContactsCreated += data.contactsCreated || 0
            } else {
              const error = await response.json()
              console.error('Outlook calendar sync failed:', error.error)
              toast.error(`Outlook sync failed: ${error.error}`)
            }
          } else {
            console.warn('⚠️  Outlook token not available')
            toast.warning('Outlook token expired. Please reconnect your account.')
          }
        } catch (error) {
          console.error('Outlook calendar sync error:', error)
          toast.error(`Outlook sync error: ${error instanceof Error ? error.message : 'Unknown error'}`)
        }
      }

      console.log(`✅ Total calendar events synced: ${totalSynced}`)
      console.log(`✅ Total contacts created: ${totalContactsCreated}`)
      
      // Update sync info for UI indicator
      if (totalSynced > 0 || totalContactsCreated > 0) {
        setLastSyncInfo({
          synced: totalSynced,
          contactsCreated: totalContactsCreated
        })
      }
      
      return { synced: totalSynced, contactsCreated: totalContactsCreated }
    } catch (error) {
      console.error('Calendar sync error:', error)
      toast.error(`Sync failed: ${error instanceof Error ? error.message : 'Unknown error'}`)
      return { synced: 0, contactsCreated: 0 }
    }
  }

  const loadEvents = async (userId: string, skipSync: boolean = false) => {
    try {
      setLoading(true)
      
      // Skip automatic sync if requested (e.g., after manual sync)
      if (!skipSync) {
        try {
          await calendarService.syncCalendarEvents(userId)
        } catch (syncError) {
          console.log('Calendar sync skipped')
        }
      }
      
      // Load events from calendar_events_enhanced table
      console.log('📅 Loading calendar events for user:', userId)
      console.log('📅 User ID type:', typeof userId)
      console.log('📅 User ID value:', userId)
      
      // Get date range for loading - past 90 days to future 365 days
      const today = new Date()
      today.setHours(0, 0, 0, 0)
      
      const startDate = new Date(today)
      startDate.setDate(today.getDate() - 90) // 90 days ago
      
      const endDate = new Date(today)
      endDate.setDate(today.getDate() + 365) // 365 days ahead
      endDate.setHours(23, 59, 59, 999)
      
      console.log('📅 Loading events from:', startDate.toISOString())
      console.log('📅 Loading events to:', endDate.toISOString())
      
      // Load all events without date filtering first
      const dbEvents = await blink.db.calendarEventsEnhanced.list({
        where: { userId },
        orderBy: { startTime: 'asc' },
        limit: 2000
      })
      console.log('📅 Raw DB events loaded:', dbEvents.length, 'events')
      
      // IMPORTANT: Blink SDK returns camelCase fields, NOT snake_case
      // So we only need to check camelCase field names
      const filteredEvents = dbEvents.filter((e: any) => {
        const startTimeValue = e.startTime || e.startDate
        if (!startTimeValue) {
          console.warn('⚠️ Event missing start time:', e.id)
          console.warn('⚠️ Event object keys:', Object.keys(e))
          return false
        }
        const eventStart = new Date(startTimeValue)
        const isInRange = eventStart >= startDate && eventStart <= endDate
        if (!isInRange) {
          console.log(`📅 Event "${e.title}" outside range: ${eventStart.toISOString()}`)
        }
        return isInRange
      })
      console.log('📅 Events in date range:', filteredEvents.length, 'events')
      
      // Debug: Show first few events with detailed field inspection
      if (dbEvents.length > 0) {
        console.log('📅 Sample event (first 3):', dbEvents.slice(0, 3))
        console.log('📅 Event fields:', Object.keys(dbEvents[0]))
        console.log('📅 First event startTime:', dbEvents[0].startTime)
        console.log('📅 First event startDate:', (dbEvents[0] as any).startDate)
        console.log('📅 Date range filter:', { startDate: startDate.toISOString(), endDate: endDate.toISOString() })
      } else {
        console.log('⚠️  No events found in calendar_events_enhanced table')
        console.log('⚠️  This could mean:')
        console.log('   1. No calendar sync has run yet')
        console.log('   2. User ID mismatch between auth and database')
        console.log('   3. No events in connected calendar accounts')
        console.log('   4. Check if OAuth connections are properly set up')
      }

      console.log('📅 Transforming events to CalendarEvent format...')
      // Transform to CalendarEvent format - Blink SDK returns camelCase
      const transformedEvents: CalendarEvent[] = filteredEvents.map((e: any) => {
        let attendeesList: string[] = []
        try {
          if (e.attendees) {
            const parsed = typeof e.attendees === 'string' ? safeJsonParse(e.attendees) : e.attendees
            attendeesList = Array.isArray(parsed) ? parsed.map((a: any) => a.email || a) : []
          }
        } catch {
          attendeesList = []
        }

        let emailLinksList: string[] = []
        try {
          if (e.emailLinks) {
            emailLinksList = typeof e.emailLinks === 'string' ? safeJsonParse(e.emailLinks) : e.emailLinks
          }
        } catch {
          emailLinksList = []
        }

        // Blink SDK auto-converts to camelCase - use camelCase fields only
        const isAllDayEvent = Number(e.isAllDay) > 0
        
        // Use the correct date/time fields from enhanced schema
        const startTimeValue = e.startTime || e.startDate
        const endTimeValue = e.endTime || e.endDate
        
        if (!startTimeValue || !endTimeValue) {
          console.warn('⚠️ Event missing time fields:', e.id, { startTime: e.startTime, endTime: e.endTime, startDate: e.startDate, endDate: e.endDate })
          return null
        }
        
        const startDateTime = new Date(startTimeValue)
        const endDateTime = new Date(endTimeValue)
        
        // All fields are in camelCase from Blink SDK
        const userId = e.userId
        const eventType = e.eventType || 'event'
        const aiPriority = e.aiPriority
        const aiSummary = e.aiSummary
        const category = e.category || aiPriority || undefined
        const color = e.color || undefined
        const createdAt = e.createdAt
        const updatedAt = e.updatedAt
        
        return {
          id: e.id,
          userId,
          title: e.title || '(No Title)',
          description: e.description || undefined,
          startTime: startDateTime,
          endTime: endDateTime,
          // Add aliases for view components
          start: startDateTime,
          end: endDateTime,
          eventType: eventType as any,
          location: e.location || undefined,
          attendees: attendeesList,
          isAllDay: isAllDayEvent,
          allDay: isAllDayEvent, // Alias for view components
          reminderMinutes: 0, // Can parse from reminders JSON if needed
          color: color,
          category: category,
          aiSummary: aiSummary || undefined,
          emailLinks: emailLinksList.length > 0 ? emailLinksList : undefined,
          createdAt: new Date(createdAt),
          updatedAt: new Date(updatedAt),
          meetingLink: e.location?.includes('zoom.us') || e.location?.includes('meet.google.com') ? e.location : undefined
        }
      })

      console.log('📅 Transformed events:', transformedEvents.length, 'events')
      
      // Filter out any null events from transformation errors
      const validEvents = transformedEvents.filter(e => e !== null)
      console.log('📅 Valid events after filter:', validEvents.length)
      
      setEvents(validEvents)
    } catch (error) {
      console.error('❌ Error loading events:', error)
      toast.error('Failed to load events')
    } finally {
      setLoading(false)
    }
  }

  // Auto-categorize events with AI
  const categorizeAllEvents = async () => {
    if (isDemoMode) {
      toast.error('Please sign in to use AI categorization')
      return
    }

    if (!user) {
      toast.error('Please sign in to use AI features')
      return
    }

    try {
      setCategorizingEvents(true)
      const loadingToast = toast.loading('Categorizing events with AI...')
      
      const uncategorizedEvents = events.filter(e => !e.category)
      
      if (uncategorizedEvents.length === 0) {
        toast.dismiss(loadingToast)
        toast.info('All events are already categorized')
        return
      }

      console.log(`Categorizing ${uncategorizedEvents.length} events...`)
      
      const categories = await calendarAI.categorizeEvents(uncategorizedEvents)
      
      // Update events with categories in parallel
      // Filter out events that no longer exist first
      const existingEventIds = await Promise.all(
        Array.from(categories.keys()).map(async (eventId) => {
          const event = await calendarService.getEvent(eventId);
          return event ? eventId : null;
        })
      );
      
      const validEventIds = existingEventIds.filter((id): id is string => id !== null);
      
      const updatePromises = validEventIds.map((eventId) => {
        const category = categories.get(eventId);
        if (!category) return Promise.resolve(null);
        return calendarService.updateEvent(eventId, { category }).catch(err => {
          console.error(`Failed to update event ${eventId}:`, err);
          return null;
        });
      });
      
      await Promise.all(updatePromises)
      
      toast.dismiss(loadingToast)
      
      const successCount = Array.from(categories.values()).filter(c => c !== 'Uncategorized').length
      
      if (successCount > 0) {
        toast.success(`Successfully categorized ${successCount} of ${uncategorizedEvents.length} events`)
      } else {
        toast.warning('Unable to categorize events - AI rate limit reached. Please try again in a few minutes.')
      }
      
      loadEvents(user.id)
    } catch (error: any) {
      console.error('Error categorizing events:', error)
      
      // Provide specific error messages
      if (error?.message?.includes('RATE_LIMITED')) {
        toast.error('AI rate limit reached. Please try again in a few minutes.')
      } else {
        toast.error('Failed to categorize events. Please try again.')
      }
    } finally {
      setCategorizingEvents(false)
    }
  }

  const handleCreateEvent = async () => {
    // Prevent duplicate submissions
    if (creatingEvent) {
      return
    }

    if (!newEvent.title || !newEvent.startTime || !newEvent.endTime) {
      toast.error('Please fill in required fields')
      return
    }

    const attendeesList = newEvent.attendees 
      ? newEvent.attendees.split(',').map(e => e.trim()).filter(e => e)
      : []

    const eventData = {
      title: newEvent.title,
      description: newEvent.description,
      startTime: new Date(newEvent.startTime),
      endTime: new Date(newEvent.endTime),
      eventType: newEvent.eventType,
      location: newEvent.location,
      attendees: attendeesList
    }

    // Demo mode: optimistic UI update
    if (isDemoMode) {
      const tempEvent: CalendarEvent = {
        id: `temp-${Date.now()}`,
        userId: 'demo-user',
        ...eventData,
        isAllDay: false,
        createdAt: new Date(),
        updatedAt: new Date()
      }
      
      setEvents(prev => [...prev, tempEvent])
      toast.success('Event created (demo mode)')
      setShowNewEventDialog(false)
      setNewEvent({
        title: '',
        description: '',
        startTime: '',
        endTime: '',
        eventType: 'event',
        location: '',
        attendees: ''
      })
      return
    }

    // Real mode: persist to database
    if (!user) {
      toast.error('Please sign in with Google or Microsoft to create events')
      return
    }

    try {
      setCreatingEvent(true)
      
      // Create event first (this is the critical action)
      const createdEvent = await calendarService.createEvent(user.id, eventData)

      toast.success('Event created successfully')
      setShowNewEventDialog(false)
      setNewEvent({
        title: '',
        description: '',
        startTime: '',
        endTime: '',
        eventType: 'event',
        location: '',
        attendees: ''
      })
      
      // Reload events immediately
      await loadEvents(user.id)

      // AI operations run asynchronously in background (non-blocking)
      // If they fail due to rate limits, event is already created
      setTimeout(async () => {
        try {
          const aiPromises: Promise<any>[] = []
          
          // Queue all AI operations
          aiPromises.push(
            calendarAI.categorizeEvent(createdEvent)
              .then(category => ({ category }))
              .catch(err => {
                console.log('Skipping AI categorization (rate limited or failed)')
                return { category: null }
              })
          )
          
          aiPromises.push(
            calendarAI.generateEventSummary(createdEvent)
              .then(summary => ({ summary }))
              .catch(err => {
                console.log('Skipping AI summary (rate limited or failed)')
                return { summary: null }
              })
          )
          
          aiPromises.push(
            calendarAI.findRelatedEmails(createdEvent)
              .then(emailLinks => ({ emailLinks }))
              .catch(err => {
                console.log('Skipping email linking (rate limited or failed)')
                return { emailLinks: null }
              })
          )
          
          // Wait for all AI operations (they handle their own errors)
          const results = await Promise.all(aiPromises)
          
          // Update event with AI results (only if we got valid results)
          const updates: any = {}
          if (results[0].category) updates.category = results[0].category
          if (results[1].summary) updates.aiSummary = results[1].summary
          if (results[2].emailLinks && results[2].emailLinks.length > 0) {
            updates.emailLinks = results[2].emailLinks
          }
          
          // Only update if we have at least one AI result
          if (Object.keys(updates).length > 0) {
            await calendarService.updateEvent(createdEvent.id, updates)
            // Reload to show AI enhancements
            await loadEvents(user.id)
          }
        } catch (error) {
          // Silently fail - event is already created
          console.log('AI enhancements completed with partial results')
        }
      }, 100)
      
    } catch (error) {
      console.error('Error creating event:', error)
      toast.error('Failed to create event')
    } finally {
      setCreatingEvent(false)
    }
  }

  const handleUpdateEvent = async () => {
    if (!editingEvent) {
      return
    }

    const attendeesList = newEvent.attendees 
      ? newEvent.attendees.split(',').map(e => e.trim()).filter(e => e)
      : []

    const updateData = {
      title: newEvent.title,
      description: newEvent.description,
      startTime: new Date(newEvent.startTime),
      endTime: new Date(newEvent.endTime),
      eventType: newEvent.eventType,
      location: newEvent.location,
      attendees: attendeesList
    }

    // Demo mode: optimistic UI update
    if (isDemoMode) {
      setEvents(prev => prev.map(e => 
        e.id === editingEvent.id 
          ? { ...e, ...updateData, updatedAt: new Date() }
          : e
      ))
      toast.success('Event updated (demo mode)')
      setEditingEvent(null)
      setNewEvent({
        title: '',
        description: '',
        startTime: '',
        endTime: '',
        eventType: 'event',
        location: '',
        attendees: ''
      })
      return
    }

    // Real mode: persist to database
    if (!user) {
      toast.error('Please sign in with Google or Microsoft to edit events')
      return
    }

    try {
      await calendarService.updateEvent(editingEvent.id, updateData)

      toast.success('Event updated successfully')
      setEditingEvent(null)
      setNewEvent({
        title: '',
        description: '',
        startTime: '',
        endTime: '',
        eventType: 'event',
        location: '',
        attendees: ''
      })
      loadEvents(user.id)
    } catch (error) {
      console.error('Error updating event:', error)
      toast.error('Failed to update event')
    }
  }

  const handleDeleteEvent = async (eventId: string) => {
    // Demo mode: optimistic UI update
    if (isDemoMode) {
      setEvents(prev => prev.filter(e => e.id !== eventId))
      toast.success('Event deleted (demo mode)')
      return
    }

    // Real mode: persist to database
    if (!user) {
      toast.error('Please sign in with Google or Microsoft to delete events')
      return
    }

    try {
      await calendarService.deleteEvent(eventId)
      toast.success('Event deleted successfully')
      loadEvents(user.id)
    } catch (error) {
      console.error('Error deleting event:', error)
      toast.error('Failed to delete event')
    }
  }

  const openEditDialog = (event: CalendarEvent) => {
    console.log('📝 Opening edit dialog for event:', event.id, event.title)
    setEditingEvent(event)
    setNewEvent({
      title: event.title,
      description: event.description || '',
      startTime: event.startTime.toISOString().slice(0, 16),
      endTime: event.endTime.toISOString().slice(0, 16),
      eventType: event.eventType,
      location: event.location || '',
      attendees: event.attendees?.join(', ') || ''
    })
  }

  // Handle event operations from advanced calendar
  const handleAdvancedEventCreate = async (eventData: any) => {
    if (isDemoMode) {
      const tempEvent: CalendarEvent = {
        id: `temp-${Date.now()}`,
        userId: 'demo-user',
        title: eventData.title,
        description: '',
        startTime: new Date(eventData.startTime),
        endTime: new Date(eventData.endTime),
        eventType: eventData.eventType || 'event',
        location: eventData.location || '',
        attendees: [],
        isAllDay: eventData.isAllDay || false,
        createdAt: new Date(),
        updatedAt: new Date()
      }
      setEvents(prev => [...prev, tempEvent])
      toast.success('Event created (demo mode)')
      return
    }

    if (!user) {
      toast.error('Please sign in to create events')
      return
    }

    try {
      const createdEvent = await calendarService.createEvent(user.id, {
        title: eventData.title,
        description: '',
        startTime: new Date(eventData.startTime),
        endTime: new Date(eventData.endTime),
        eventType: eventData.eventType || 'event',
        location: eventData.location || '',
        attendees: []
      })
      
      toast.success('Event created successfully')
      await loadEvents(user.id)
    } catch (error) {
      console.error('Error creating event:', error)
      toast.error('Failed to create event')
    }
  }

  const handleAdvancedEventUpdate = async (eventData: any) => {
    if (isDemoMode) {
      setEvents(prev => prev.map(e => 
        e.id === eventData.id 
          ? { ...e, ...eventData, updatedAt: new Date() }
          : e
      ))
      toast.success('Event updated (demo mode)')
      return
    }

    if (!user) {
      toast.error('Please sign in to update events')
      return
    }

    try {
      await calendarService.updateEvent(eventData.id, eventData)
      toast.success('Event updated successfully')
      await loadEvents(user.id)
    } catch (error) {
      console.error('Error updating event:', error)
      toast.error('Failed to update event')
    }
  }

  const handleAdvancedEventDelete = async (eventId: string) => {
    await handleDeleteEvent(eventId)
  }

  const handleAdvancedEventClick = (event: CalendarEvent) => {
    openEditDialog(event)
  }
  
  const handleSchedulePrep = async (prepSlots: any[]) => {
    if (!user) {
      toast.error('Please sign in to schedule prep time')
      return
    }
    
    try {
      // Create prep time blocks for each meeting with proper category and color
      for (const slot of prepSlots) {
        await calendarService.createEvent(user.id, {
          title: `📝 Prep: ${slot.meetingTitle}`,
          description: `Preparation time for upcoming meeting`,
          startTime: slot.prepStart,
          endTime: slot.prepEnd,
          eventType: 'task',
          location: '',
          attendees: [],
          category: 'Work',
          color: '#3B82F6' // Blue color for prep tasks
        })
      }
      
      // Reload events to show the newly created prep blocks
      await loadEvents(user.id)
      
      toast.success(`Successfully scheduled ${prepSlots.length} prep session${prepSlots.length > 1 ? 's' : ''}!`, {
        description: 'Your prep time blocks have been added to the calendar'
      })
    } catch (error) {
      console.error('Error scheduling prep time:', error)
      toast.error('Failed to schedule prep time')
    }
  }

  const getEventIcon = (type: string) => {
    switch (type) {
      case 'meeting': return <Users className="w-4 h-4" />
      case 'reminder': return <Clock className="w-4 h-4" />
      case 'task': return <CalendarIcon className="w-4 h-4" />
      default: return <CalendarIcon className="w-4 h-4" />
    }
  }

  const getEventColor = (type: string) => {
    switch (type) {
      case 'meeting': return 'from-blue-500/20 to-cyan-500/20'
      case 'reminder': return 'from-amber-500/20 to-orange-500/20'
      case 'task': return 'from-green-500/20 to-emerald-500/20'
      default: return 'from-purple-500/20 to-pink-500/20'
    }
  }

  const getCategoryColor = (category?: string) => {
    if (!category) return 'bg-gray-500/10 text-gray-600'
    
    const colors: Record<string, string> = {
      'Work': 'bg-blue-500/10 text-blue-600',
      'Personal': 'bg-purple-500/10 text-purple-600',
      'Health': 'bg-green-500/10 text-green-600',
      'Education': 'bg-orange-500/10 text-orange-600',
      'Social': 'bg-pink-500/10 text-pink-600',
      'Finance': 'bg-emerald-500/10 text-emerald-600',
      'Travel': 'bg-cyan-500/10 text-cyan-600',
      'Shopping': 'bg-amber-500/10 text-amber-600',
      'Entertainment': 'bg-rose-500/10 text-rose-600',
      'Family': 'bg-indigo-500/10 text-indigo-600'
    }
    
    return colors[category] || 'bg-gray-500/10 text-gray-600'
  }

  // Removed unused formatViewTitle function - not needed with AdvancedCalendar

  // Render event in different views
  const renderEventCard = (event: CalendarEvent, compact: boolean = false) => (
    <motion.div
      key={event.id}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`glass-card rounded-lg p-3 bg-gradient-to-br ${getEventColor(event.eventType)} group relative cursor-pointer hover:scale-[1.02] transition-transform`}
    >
      <div className="flex items-start gap-2">
        <div className="p-1.5 glass rounded-md flex-shrink-0">
          {getEventIcon(event.eventType)}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <h3 className={`font-semibold ${compact ? 'text-xs' : 'text-sm'} truncate`}>{event.title}</h3>
            <Badge variant="outline" className="text-xs flex-shrink-0">
              {event.eventType}
            </Badge>
          </div>
          
          {!compact && (
            <>
              <div className="space-y-1 text-xs text-muted-foreground mb-2">
                <div className="flex items-center gap-2">
                  <Clock className="w-3 h-3 flex-shrink-0" />
                  <span className="truncate">
                    {event.startTime.toLocaleTimeString('en-US', { 
                      hour: 'numeric', 
                      minute: '2-digit' 
                    })} - {event.endTime.toLocaleTimeString('en-US', { 
                      hour: 'numeric', 
                      minute: '2-digit' 
                    })}
                  </span>
                </div>
                
                {event.location && (
                  <div className="flex items-center gap-2">
                    {event.location.includes('Zoom') || event.location.includes('Meet') ? (
                      <Video className="w-3 h-3 flex-shrink-0" />
                    ) : (
                      <MapPin className="w-3 h-3 flex-shrink-0" />
                    )}
                    <span className="truncate">{event.location}</span>
                  </div>
                )}
                
                {event.attendees && event.attendees.length > 0 && (
                  <div className="flex items-center gap-2">
                    <Users className="w-3 h-3 flex-shrink-0" />
                    <span className="truncate">{event.attendees.length} attendees</span>
                  </div>
                )}
              </div>

              {/* AI Features */}
              <div className="flex items-center gap-2 flex-wrap">
                {event.category && (
                  <Badge variant="secondary" className={`text-xs ${getCategoryColor(event.category)}`}>
                    <Tag className="w-3 h-3 mr-1" />
                    {event.category}
                  </Badge>
                )}
                {event.emailLinks && event.emailLinks.length > 0 && (
                  <Badge variant="secondary" className="text-xs bg-blue-500/10 text-blue-600">
                    <Mail className="w-3 h-3 mr-1" />
                    {event.emailLinks.length} emails
                  </Badge>
                )}
                {event.aiSummary && (
                  <Badge variant="secondary" className="text-xs bg-purple-500/10 text-purple-600">
                    <Sparkles className="w-3 h-3 mr-1" />
                    AI
                  </Badge>
                )}
              </div>
            </>
          )}
        </div>
        <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
          <Button
            size="sm"
            variant="ghost"
            className="h-7 w-7 p-0"
            onClick={(e) => {
              e.stopPropagation()
              openEditDialog(event)
            }}
          >
            <Edit className="w-3 h-3" />
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="h-7 w-7 p-0 text-destructive hover:text-destructive"
            onClick={(e) => {
              e.stopPropagation()
              handleDeleteEvent(event.id)
            }}
          >
            <Trash2 className="w-3 h-3" />
          </Button>
        </div>
      </div>
    </motion.div>
  )

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-3">
          <div className="p-3 glass rounded-xl">
            <CalendarIcon className="w-6 h-6 text-primary" />
          </div>
          <div>
            <h1 className="text-3xl font-bold gradient-text">Calendar</h1>
            <p className="text-muted-foreground">
              {isDemoMode ? 'Demo Mode - Sign in to sync your calendar' : 'Manage your schedule with AI intelligence'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {!isDemoMode && (
            <Button 
              variant="outline" 
              className="gap-2"
              onClick={categorizeAllEvents}
              disabled={categorizingEvents}
            >
              <Sparkles className="w-4 h-4" />
              AI Categorize
            </Button>
          )}
          <Button className="gap-2" onClick={() => setShowNewEventDialog(true)}>
            <Plus className="w-4 h-4" />
            New Event
          </Button>
        </div>
      </div>

      {/* Meeting Prep Scheduler */}
      {!isDemoMode && (
        <MeetingPrepScheduler 
          events={events}
          onSchedulePrep={handleSchedulePrep}
        />
      )}
      
      {/* Demo Mode Banner */}
      {isDemoMode && (
        <Card className="p-4 glass-card border-primary/20">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary/10">
              <CalendarIcon className="w-5 h-5 text-primary" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-sm">Demo Mode</h3>
              <p className="text-xs text-muted-foreground">
                You're viewing sample events. Sign in to sync your real calendar and use AI categorization & email linking.
              </p>
            </div>
          </div>
        </Card>
      )}

      {/* Sync Info Indicator */}
      {!isDemoMode && lastSyncInfo && (lastSyncInfo.synced > 0 || lastSyncInfo.contactsCreated > 0) && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass-card p-3 border-green-500/20 bg-green-500/5"
        >
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <div className="p-1.5 rounded-lg bg-green-500/10">
                <Clock className="w-4 h-4 text-green-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-green-600">Last Sync Successful</p>
                <p className="text-xs text-muted-foreground">
                  {lastSyncInfo.synced} events synced
                  {lastSyncInfo.contactsCreated > 0 && `, ${lastSyncInfo.contactsCreated} contacts created`}
                </p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs"
              onClick={() => setLastSyncInfo(null)}
            >
              Dismiss
            </Button>
          </div>
        </motion.div>
      )}

      {/* Advanced Calendar */}
      <AdvancedCalendar
        events={events}
        onEventCreate={handleAdvancedEventCreate}
        onEventUpdate={handleAdvancedEventUpdate}
        onEventDelete={handleAdvancedEventDelete}
        onEventClick={handleAdvancedEventClick}
        startDayOfWeek={startDayOfWeek}
        weekOptions={{
          showNowIndicator: true,
          narrowWeekend: narrowWeekend,
          hourStart: 0,
          hourEnd: 24
        }}
        monthOptions={{
          visibleWeeksCount: 6
        }}
      />

      {/* Quick Stats */}
      <Card className="p-4 glass-card">
        <h3 className="text-sm font-semibold mb-3">Quick Stats</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center p-3 glass rounded-lg">
            <div className="text-2xl font-bold text-primary">{events.length}</div>
            <div className="text-xs text-muted-foreground">Total Events</div>
          </div>
          <div className="text-center p-3 glass rounded-lg">
            <div className="text-2xl font-bold text-blue-500">{events.filter(e => e.eventType === 'meeting').length}</div>
            <div className="text-xs text-muted-foreground">Meetings</div>
          </div>
          <div className="text-center p-3 glass rounded-lg">
            <div className="text-2xl font-bold text-amber-500">{events.filter(e => e.eventType === 'reminder').length}</div>
            <div className="text-xs text-muted-foreground">Reminders</div>
          </div>
          <div className="text-center p-3 glass rounded-lg">
            <div className="text-2xl font-bold text-green-500">{events.filter(e => e.eventType === 'task').length}</div>
            <div className="text-xs text-muted-foreground">Tasks</div>
          </div>
        </div>
      </Card>

      {/* New/Edit Event Dialog */}
      <Dialog open={showNewEventDialog || editingEvent !== null} onOpenChange={(open) => {
        if (!open) {
          setShowNewEventDialog(false)
          setEditingEvent(null)
          setNewEvent({
            title: '',
            description: '',
            startTime: '',
            endTime: '',
            eventType: 'event',
            location: '',
            attendees: ''
          })
        }
      }}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>{editingEvent ? 'Edit Event' : 'New Event'}</DialogTitle>
            <DialogDescription>
              {editingEvent ? 'Update the event details below.' : 'Create a new calendar event with AI-powered categorization.'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="title">Title *</Label>
              <Input
                id="title"
                value={newEvent.title}
                onChange={(e) => setNewEvent({ ...newEvent, title: e.target.value })}
                placeholder="Event title"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={newEvent.description}
                onChange={(e) => setNewEvent({ ...newEvent, description: e.target.value })}
                placeholder="Event description"
                rows={3}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="startTime">Start Time *</Label>
                <Input
                  id="startTime"
                  type="datetime-local"
                  value={newEvent.startTime}
                  onChange={(e) => setNewEvent({ ...newEvent, startTime: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="endTime">End Time *</Label>
                <Input
                  id="endTime"
                  type="datetime-local"
                  value={newEvent.endTime}
                  onChange={(e) => setNewEvent({ ...newEvent, endTime: e.target.value })}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="eventType">Event Type</Label>
              <Select value={newEvent.eventType} onValueChange={(value: any) => setNewEvent({ ...newEvent, eventType: value })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="event">Event</SelectItem>
                  <SelectItem value="meeting">Meeting</SelectItem>
                  <SelectItem value="reminder">Reminder</SelectItem>
                  <SelectItem value="task">Task</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="location">Location</Label>
              <Input
                id="location"
                value={newEvent.location}
                onChange={(e) => setNewEvent({ ...newEvent, location: e.target.value })}
                placeholder="Location or meeting link"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="attendees">Attendees</Label>
              <Input
                id="attendees"
                value={newEvent.attendees}
                onChange={(e) => setNewEvent({ ...newEvent, attendees: e.target.value })}
                placeholder="email1@example.com, email2@example.com"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowNewEventDialog(false)
              setEditingEvent(null)
            }}>
              Cancel
            </Button>
            <Button 
              onClick={editingEvent ? handleUpdateEvent : handleCreateEvent}
              disabled={creatingEvent}
            >
              {creatingEvent ? 'Creating...' : (editingEvent ? 'Update Event' : 'Create Event')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
